'use client';
import { Stars as Cosmos } from '@react-three/drei';
import { GroupProps, useFrame } from '@react-three/fiber';
import { forwardRef, useRef, useState } from 'react';
import type { Group, Points } from 'three';

export interface StarsProps extends GroupProps {
  rotationSpeed?: number;
}
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const Stars = forwardRef<Group, StarsProps>((props: StarsProps, ref) => {
  const cosmosRef = useRef<Points | null>(null);
  const groupRef = useRef<Group | null>(null);
  const [animationProgress, setAnimationProgress] = useState(0);
  const duration = 6;
  const startPosition: [number, number, number] = [20, 40, -120];
  const endPosition: [number, number, number] = [0, 0, 0];

  useFrame((_, delta) => {
    if (animationProgress < 1) {
      setAnimationProgress(prev => Math.min(prev + delta / duration, 1));

      const easedProgress = easeOutCubic(animationProgress);

      if (groupRef.current) {
        const lerp = (start: number, end: number, t: number) =>
          start + t * (end - start);
        groupRef.current.position.set(
          lerp(startPosition[0], endPosition[0], easedProgress),
          lerp(startPosition[1], endPosition[1], easedProgress),
          lerp(startPosition[2], endPosition[2], easedProgress)
        );
      }
    }

    if (cosmosRef.current && props.rotationSpeed) {
      cosmosRef.current.rotation.y += props.rotationSpeed;
    }
  });

  return (
    <group ref={groupRef} position={startPosition} {...ref}>
      <Cosmos
        ref={cosmosRef}
        count={300}
        depth={0.5}
        radius={5}
        fade
        factor={0.35}
        speed={0}
        saturation={1}
      />
    </group>
  );
});

Stars.displayName = 'Stars';
export default Stars;
